import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../../widgets/Layout';
import { commonStyles, theme } from '../../../shared/lib/styles';
import { playlistAPI } from '../../../shared/api/api';

const PlaylistsPage = () => {
    const navigate = useNavigate();
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadPlaylists = async () => {
        setLoading(true);
        setError('');
        const result = await playlistAPI.getAll();
        if (result.success) {
            setPlaylists(Array.isArray(result.data) ? result.data : []);
        } else {
            setError('Не удалось загрузить плейлисты');
        }
        setLoading(false);
    };

    useEffect(() => {
        loadPlaylists();
    }, []);

    const openCreate = () => {
        setModalMode('create');
        setName('');
        setShowModal(true);
    };

    const openEdit = () => {
        if (!selected) return;
        setModalMode('edit');
        setName(selected.name);
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Введите название');
            return;
        }
        setSubmitting(true);
        setError('');

        if (modalMode === 'create') {
            const result = await playlistAPI.create(trimmed);
            if (result.success) {
                setMessage('Плейлист создан');
                setShowModal(false);
                await loadPlaylists();
            } else {
                setError(
                    typeof result.error === 'string'
                        ? result.error
                        : result.error?.name?.[0] || 'Ошибка создания'
                );
            }
        } else if (selected) {
            const result = await playlistAPI.update(selected.id, trimmed);
            if (result.success) {
                setMessage('Плейлист обновлён');
                setShowModal(false);
                setSelected(null);
                await loadPlaylists();
            } else {
                setError(
                    typeof result.error === 'string'
                        ? result.error
                        : result.error?.name?.[0] || 'Ошибка обновления'
                );
            }
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!selected) return;
        if (!window.confirm(`Удалить плейлист «${selected.name}»?`)) return;

        const result = await playlistAPI.delete(selected.id);
        if (result.success) {
            setMessage('Плейлист удалён');
            setSelected(null);
            await loadPlaylists();
        } else {
            setError(result.error || 'Ошибка удаления');
        }
    };

    const formatDate = (iso) =>
        iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';

    return (
        <Layout>
            <div style={styles.container}>
                <h1 style={styles.title}>Мои плейлисты</h1>
                <Link to="/dashboard" style={styles.backLink}>
                    ← На главную
                </Link>

                {message && <div style={commonStyles.success}>{message}</div>}
                {error && !showModal && <div style={commonStyles.error}>{error}</div>}

                <div style={styles.toolbar}>
                    <button
                        type="button"
                        onClick={openCreate}
                        style={{ ...commonStyles.button, ...commonStyles.buttonPrimary }}
                    >
                        + Создать плейлист
                    </button>
                    {selected && (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate(`/playlists/${selected.id}`)}
                                style={{ ...commonStyles.button, ...commonStyles.buttonPrimary }}
                            >
                                Открыть
                            </button>
                            <button
                                type="button"
                                onClick={openEdit}
                                style={{ ...commonStyles.button, ...commonStyles.buttonPrimary }}
                            >
                                Переименовать
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                style={{ ...commonStyles.button, ...commonStyles.buttonDanger }}
                            >
                                Удалить
                            </button>
                        </>
                    )}
                </div>

                {loading ? (
                    <div style={commonStyles.loadingContainer}>
                        <div style={commonStyles.loadingSpinner} />
                    </div>
                ) : (
                    <div style={styles.tableWrapper}>
                        <table style={commonStyles.table}>
                            <thead>
                                <tr>
                                    <th style={commonStyles.tableHeader}>Название</th>
                                    <th style={commonStyles.tableHeader}>Треков</th>
                                    <th style={commonStyles.tableHeader}>Обновлён</th>
                                </tr>
                            </thead>
                            <tbody>
                                {playlists.map((p) => (
                                    <tr
                                        key={p.id}
                                        onClick={() => setSelected(p)}
                                        onDoubleClick={() => navigate(`/playlists/${p.id}`)}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor:
                                                selected?.id === p.id
                                                    ? theme.colors.tableRowAlt
                                                    : 'transparent',
                                        }}
                                    >
                                        <td style={commonStyles.tableCell}>{p.name}</td>
                                        <td style={commonStyles.tableCell}>
                                            {p.track_count ?? p.tracks?.length ?? 0}
                                        </td>
                                        <td style={commonStyles.tableCell}>
                                            {formatDate(p.updated_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {playlists.length === 0 && (
                            <p style={styles.empty}>Плейлистов пока нет. Создайте первый.</p>
                        )}
                    </div>
                )}

                {showModal && (
                    <div style={styles.overlay} onClick={() => setShowModal(false)}>
                        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                            <h2 style={styles.modalTitle}>
                                {modalMode === 'create' ? 'Новый плейлист' : 'Переименовать'}
                            </h2>
                            <form onSubmit={handleSave}>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Название"
                                    style={styles.input}
                                    autoFocus
                                />
                                {error && showModal && (
                                    <div style={commonStyles.error}>{error}</div>
                                )}
                                <div style={styles.modalActions}>
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        style={{
                                            ...commonStyles.button,
                                            ...commonStyles.buttonSecondary,
                                        }}
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        style={{
                                            ...commonStyles.button,
                                            ...commonStyles.buttonPrimary,
                                        }}
                                    >
                                        {submitting ? 'Сохранение…' : 'Сохранить'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

const styles = {
    container: commonStyles.container,
    title: commonStyles.title,
    backLink: {
        display: 'inline-block',
        marginBottom: theme.spacing.lg,
        color: theme.colors.primary,
        textDecoration: 'none',
        fontSize: theme.fontSize.sm,
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing.md,
        marginBottom: theme.spacing.lg,
    },
    tableWrapper: commonStyles.tableWrapper,
    empty: {
        textAlign: 'center',
        padding: theme.spacing.xl,
        color: theme.colors.textSecondary,
    },
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: theme.colors.bgPrimary,
        padding: theme.spacing.xl,
        borderRadius: theme.borderRadius.lg,
        minWidth: 320,
        boxShadow: theme.shadows.lg,
    },
    modalTitle: {
        marginTop: 0,
        marginBottom: theme.spacing.md,
    },
    input: {
        width: '100%',
        padding: theme.spacing.sm,
        marginBottom: theme.spacing.md,
        border: `1px solid ${theme.colors.borderMedium}`,
        borderRadius: theme.borderRadius.md,
        boxSizing: 'border-box',
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.md,
    },
};

export default PlaylistsPage;
